import type { CustomFieldType, FieldWidth, IOverrideOption, IRecordTypeOverride } from '@platform/core/types';
import type { IBuilderFieldRow } from './IBuilderFieldRow';
import { removeCustomField, withCustomField, withFieldOverride } from './patchFields';

/** Значення, рівне опублікованому, — без перевизначення (undefined), щоб патч містив лише відмінності. */
const diff = <T>(next: T, published: T): T | undefined => (next === published ? undefined : next);

/**
 * Зміни рядка «Полів» як функції патча (чисті): власне поле — у `customFields`, спроєктоване — у `fields.<ключ>` лише відмінностями від
 * опублікованого визначення; вилучення власного поля чи приховування спроєктованого.
 */
export const fieldEdits = {
  required: (row: IBuilderFieldRow, on: boolean) => (p: IRecordTypeOverride) => (row.custom
    ? withCustomField(p, row.field.key, { required: on || undefined })
    : withFieldOverride(p, row.field.key, { required: diff(on, !!row.published?.required) })),
  inList: (row: IBuilderFieldRow, on: boolean) => (p: IRecordTypeOverride) => (row.custom
    ? withCustomField(p, row.field.key, { inList: on || undefined })
    : withFieldOverride(p, row.field.key, { inList: diff(on, !!row.published?.inList) })),
  width: (row: IBuilderFieldRow, width: FieldWidth | null) => (p: IRecordTypeOverride) => (row.custom
    ? withCustomField(p, row.field.key, { width: width ?? undefined })
    : withFieldOverride(p, row.field.key, { width: diff<FieldWidth | null>(width, row.published?.width ?? null) })),
  options: (row: IBuilderFieldRow, options: IOverrideOption[]) => (p: IRecordTypeOverride) => (row.custom
    ? withCustomField(p, row.field.key, { options })
    : withFieldOverride(p, row.field.key, { options })),
  /** Тип власного поля: вибір отримує варіанти, «Користувач» (учасник простору, не персональні дані) знімає «Персональні дані». */
  type: (row: IBuilderFieldRow, type: CustomFieldType, selectOptions: () => IOverrideOption[]) => (p: IRecordTypeOverride) => withCustomField(p, row.field.key, {
    type, options: type === 'select' ? selectOptions() : undefined, ...(type === 'user' ? { pii: undefined } : {}),
  }),
  /** «Персональні дані» — лише власне поле (ADR-023 customFields.pii; у перевизначенні спроєктованого поля ключа pii немає). */
  pii: (row: IBuilderFieldRow, on: boolean) => (p: IRecordTypeOverride) => (row.custom ? withCustomField(p, row.field.key, { pii: on || undefined }) : p),
  remove: (row: IBuilderFieldRow) => (p: IRecordTypeOverride) => (row.custom ? removeCustomField(p, row.field.key) : withFieldOverride(p, row.field.key, { hidden: true })),
  show: (key: string) => (p: IRecordTypeOverride) => withFieldOverride(p, key, { hidden: undefined }),
};
